import type { RoiId } from '~/domain/changes/ids';
import { Divider, Skeleton, Stack, Typography } from '@mui/material';
import { Fragment, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import AxisTripletValue from '~/components/Fields/AxisTripletValue';
import CopyField from '~/components/Fields/CopyField';
import { LabelText } from '~/components/Fields/LabeledValue';
import PropertiesPanel from '~/components/Properties/PropertiesPanel';
import { useRoiIntensityStatistics } from '~/hooks/structure-set/useRoiIntensityStatistics';
import { useRoiStatistics } from '~/hooks/structure-set/useRoiStatistics';
import {
    roiIntensityStatRows,
    roiStatRows,
    STAT_ROW_PLACEHOLDERS,
} from './roiStatRows';

interface StructureStatisticsProps {
    roiId: RoiId;
}

/**
 * Whether the browser has painted since `key` was first seen. An animation
 * frame callback runs before the next paint, so the timeout it sets runs
 * after that paint.
 */
function usePaintedSince(key: string): boolean {
    const [paintedKey, setPaintedKey] = useState<string>();
    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout> | undefined;
        const frame = requestAnimationFrame(() => {
            timeout = setTimeout(() => setPaintedKey(key));
        });
        return () => {
            cancelAnimationFrame(frame);
            clearTimeout(timeout);
        };
    }, [key]);
    return paintedKey === key;
}

/**
 * "Statistics" section of the structure Properties panel: shape figures
 * from the ROI's SDF, then the image intensity under the mask. Renders nothing
 * without shape statistics; intensity rows are absent, never zero, with no
 * image.
 *
 * The figures are worked out on the main thread over every voxel of the
 * mask, which takes long on a large structure the first time. So the
 * section first shows its rows with placeholders, and works the figures out
 * only once the panel is on screen.
 */
export default function StructureStatistics({
    roiId,
}: StructureStatisticsProps) {
    const painted = usePaintedSince(roiId);
    const shapeStats = useRoiStatistics(painted ? roiId : undefined);
    const intensity = useRoiIntensityStatistics(painted ? roiId : undefined);
    if (painted && !shapeStats) {
        return null;
    }
    const rows = shapeStats
        ? [
              ...roiStatRows(shapeStats),
              ...(intensity ? roiIntensityStatRows(intensity) : []),
          ]
        : undefined;

    return (
        <Stack
            spacing={1}
            data-testid="structure-statistics"
            aria-busy={!rows}
            sx={{
                // A scalar value's copy button shows on hover just past the
                // value, in the panel's padding: it takes no width from the
                // value column.
                '& .copy-icon': {
                    position: 'absolute',
                    left: '100%',
                    p: 0.25,
                },
            }}
        >
            <Divider />
            <Typography fontSize={12} fontWeight={500}>
                <FormattedMessage id="roi.stats.title" />
            </Typography>
            <PropertiesPanel variant="panel">
                {rows
                    ? rows.map((row) => (
                          <Fragment key={row.labelId}>
                              <LabelText>
                                  <FormattedMessage id={row.labelId} />
                              </LabelText>
                              {row.kind === 'axes' ? (
                                  <AxisTripletValue
                                      axes={row.axes}
                                      unit={row.unit}
                                  />
                              ) : (
                                  <CopyField
                                      label={row.value}
                                      size={12}
                                      noWrap
                                  />
                              )}
                          </Fragment>
                      ))
                    : STAT_ROW_PLACEHOLDERS.map(({ labelId, valueWidthCh }) => (
                          <Fragment key={labelId}>
                              <LabelText>
                                  <FormattedMessage id={labelId} />
                              </LabelText>
                              {/* In the text a value is shown in, so the
                                  row keeps its height when the value comes. */}
                              <Typography fontSize={12}>
                                  <Skeleton
                                      width={`${valueWidthCh}ch`}
                                      data-testid="structure-statistic-pending"
                                  />
                              </Typography>
                          </Fragment>
                      ))}
            </PropertiesPanel>
        </Stack>
    );
}
